import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChildren, QueryList, OnChanges, SimpleChanges } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';

interface VideoInfo {
  originalUrl: string | SafeResourceUrl;
  videoId: string;
  safeUrl?: string;
}

@Component({
  selector: 'app-you-tube-player',
  templateUrl: './you-tube-player.component.html',
  styleUrls: ['./you-tube-player.component.scss']
})
export class YouTubePlayerComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input() videoUrls: (string | SafeResourceUrl)[] = [];
    
  @Input() height: number | string = 300;
  @Input() width: string = '100%';
  @Input() autoplay: boolean = false;
  @Input() controls: boolean = true;
  @Input() modestbranding: boolean = true;
  @Input() showFallback: boolean = true;
  @Input() playerClass: string = 'w-100 mt-3';
  @Input() showDeleteButton: boolean = false;
  
  @Output() playerReady = new EventEmitter<{index: number; event: any}>();
  @Output() playerError = new EventEmitter<{index: number; event: any}>();
  @Output() stateChange = new EventEmitter<{index: number; event: any}>();
  @Output() deleteVideo = new EventEmitter<number>();
  
  @ViewChildren('playerContainer') playerContainers!: QueryList<ElementRef>;
  
  private players: any[] = [];
  private youtubeApiLoaded = false;
  videoInfos: VideoInfo[] = [];
  loadingErrors: boolean[] = [];
  private isInitialized = false;
  loadingStates: boolean[] = [];

  
  constructor() {}
  
  ngOnInit() {
    this.processVideoUrls();
    this.loadYouTubeAPI();
    this.isInitialized = true;

  }
  
  ngOnChanges(changes: SimpleChanges) {
    if (this.isInitialized && (changes['videoUrls'] || changes['videoUrl'])) {
      this.destroyPlayers();
      this.processVideoUrls();
      
      setTimeout(() => {
        if (this.youtubeApiLoaded) {
          this.createPlayers();
        }
      }, 100);
    }
  }
  
  ngAfterViewInit() {
    setTimeout(() => {
      if (this.youtubeApiLoaded) {
        this.createPlayers();
      }
    }, 500);
  }
  
  ngOnDestroy() {
    this.destroyPlayers();
  }
  
  private processVideoUrls() {
    this.videoInfos = [];
    this.loadingErrors = [];
    this.loadingStates = [];    
    const urlsToProcess = this.videoUrls && this.videoUrls.length > 0 
      ? this.videoUrls 
      : [];
    
    urlsToProcess.forEach(url => {
      const videoInfo = this.extractVideoInfo(url);
      if (videoInfo.videoId) {
        this.videoInfos.push(videoInfo);
        this.loadingErrors.push(false);
        this.loadingStates.push(true);
      }
    });
  }
  
  private extractVideoInfo(url: string | SafeResourceUrl): VideoInfo {
    let urlString = '';
    let safeUrl = '';
    
    if (typeof url === 'object' && url.hasOwnProperty('changingThisBreaksApplicationSecurity')) {
      urlString = (url as any).changingThisBreaksApplicationSecurity;
      safeUrl = urlString;
    } else if (typeof url === 'string') {
      urlString = url;
      safeUrl = urlString;
    }
    
    const videoId = this.extractVideoIdFromUrl(urlString);    
    return {
      originalUrl: url,
      videoId: videoId,
      safeUrl: safeUrl
    };
  }
  
  private extractVideoIdFromUrl(url: string): string {
    if (!url) return '';
    
    const cleanUrl = url.split('#')[0];
    
    const patterns = [
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/,
      /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/,
      /youtube\.com\/embed\/([^"&?\/\s]{11})/,
      /youtu\.be\/([^"&?\/\s]{11})/
    ];
    
    for (const pattern of patterns) {
      const match = cleanUrl.match(pattern);
      if (match) {
        // Extract video ID - check different match groups
        for (let i = 1; i < match.length; i++) {
          if (match[i] && match[i].length === 11) {
            return match[i];
          }
        }
      }
    }
    
    return '';
  }
  
  private loadYouTubeAPI() {
    if (window['YT']) {
      this.youtubeApiLoaded = true;
      this.createPlayers();
      return;
    }
    
    // Check if script is already loading
    if (document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      window['onYouTubeIframeAPIReady'] = () => {
        this.youtubeApiLoaded = true;
        this.createPlayers();
      };
      return;
    }
    
    // Load YouTube IFrame API
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode!.insertBefore(tag, firstScriptTag);
    
    window['onYouTubeIframeAPIReady'] = () => {
      this.youtubeApiLoaded = true;
      this.createPlayers();
    };
  }
  
  private createPlayers() {
    if (!this.youtubeApiLoaded || !this.playerContainers || this.videoInfos.length === 0) {
      return;
    }
    
    // Clear existing players
    this.destroyPlayers();
    
    setTimeout(() => {
      this.playerContainers.forEach((container, index) => {
        if (index < this.videoInfos.length) {
          const videoInfo = this.videoInfos[index];
          
          const player = new window['YT'].Player(container.nativeElement, {
            height: this.height,
            width: this.width,
            videoId: videoInfo.videoId,
            playerVars: {
              'autoplay': this.autoplay ? 1 : 0,
              'controls': this.controls ? 1 : 0,
              'modestbranding': this.modestbranding ? 1 : 0,
              'origin': window.location.origin,
              'enablejsapi': 1,
              'rel': 0,
              'referrerpolicy': 'strict-origin-when-cross-origin'
            },
            events: {
              'onReady': (event: any) => this.onPlayerReady(event, index),
              'onStateChange': (event: any) => this.onStateChange(event, index),
              'onError': (event: any) => this.onPlayerError(event, index)
            }
          });
          
          this.players.push(player);
        }
      });
    }, 0);
  }
  
  private onPlayerReady(event: any, index: number) {
    this.loadingErrors[index] = false;
    this.loadingStates[index] = false;

    this.checkIfVideoRendered(index);


    this.playerReady.emit({ index, event });
  }

  private checkIfVideoRendered(index: number) {
  if (!this.playerContainers || index >= this.playerContainers.length) {
    return;
  }
  
  const container = this.playerContainers.toArray()[index].nativeElement;
  const iframe = container.querySelector('iframe');
  
  if (iframe) {
    if (iframe.contentDocument || iframe.contentWindow) {
       this.loadingStates[index] = false;
    } else {
      iframe.onload = () => {
         this.loadingStates[index] = false;
      };
      
      setTimeout(() => {
        if (this.loadingStates[index]) {
          console.warn(`Iframe load timeout for player ${index}`);
           this.loadingStates[index] = false;
        }
      }, 3000);
    }
  } else {
    setTimeout(() => this.checkIfVideoRendered(index), 0);
  }
}

  
  private onPlayerError(event: any, index: number) {
    console.error(`YouTube Player ${index} error:`, event.data);
    this.loadingErrors[index] = true;
    this.loadingStates[index] = false;
    this.playerError.emit({ index, event });
    
    if (this.showFallback) {
      this.showFallbackIframe(index);
    }
  }
  
  private onStateChange(event: any, index: number) {
    this.stateChange.emit({ index, event });
  }
  
  private showFallbackIframe(index: number) {
    if (!this.playerContainers || index >= this.playerContainers.length) {
      return;
    }
    
    const container = this.playerContainers.toArray()[index].nativeElement;
    const videoInfo = this.videoInfos[index];
    
    if (!container || !videoInfo || !videoInfo.videoId) {
      return;
    }
    
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube-nocookie.com/embed/${videoInfo.videoId}`;
    iframe.height = typeof this.height === 'number' ? this.height.toString() : this.height;
    iframe.width = this.width;
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.className = this.playerClass + ' youtube-iframe';
    
    container.innerHTML = '';
    container.appendChild(iframe);
  }
  
  private destroyPlayers() {
    this.players.forEach(player => {
      if (player && player.destroy) {
        player.destroy();
      }
    });
    this.players = [];
  }

  playVideo(index?: number) {
    if (index !== undefined && this.players[index] && this.players[index].playVideo) {
      this.players[index].playVideo();
    } else {
      this.players.forEach(player => {
        if (player && player.playVideo) {
          player.playVideo();
        }
      });
    }
  }
  
  pauseVideo(index?: number) {
    if (index !== undefined && this.players[index] && this.players[index].pauseVideo) {
      this.players[index].pauseVideo();
    } else {
      this.players.forEach(player => {
        if (player && player.pauseVideo) {
          player.pauseVideo();
        }
      });
    }
  }
  
  stopVideo(index?: number) {
    if (index !== undefined && this.players[index] && this.players[index].stopVideo) {
      this.players[index].stopVideo();
    } else {
      this.players.forEach(player => {
        if (player && player.stopVideo) {
          player.stopVideo();
        }
      });
    }
  }
  
  // Add a new video
  addVideo(url: string | SafeResourceUrl) {
    const videoInfo = this.extractVideoInfo(url);
    if (videoInfo.videoId) {
      this.videoInfos.push(videoInfo);
      this.loadingErrors.push(false);
      
      // Recreate players
      setTimeout(() => {
        this.destroyPlayers();
        this.createPlayers();
      }, 100);
    }
  }
  
  // Remove a video
  removeVideo(index: number) {
    if (index >= 0 && index < this.videoInfos.length) {
      // Destroy player if exists
      if (this.players[index] && this.players[index].destroy) {
        this.players[index].destroy();
      }
      
      // Remove from arrays
      this.videoInfos.splice(index, 1);
      this.loadingErrors.splice(index, 1);
      this.players.splice(index, 1);
    }
  }
  
  onDeleteClick(index: number) {
    this.deleteVideo.emit(index);
    this.removeVideo(index);
  }
  
  trackByFn(index: number, item: VideoInfo): string {
    return item.videoId || index.toString();
  }
}

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}